/**
 * The required format for a boolean flag key.
 */
/**
 * Helper function to test that enum keys and values match correct format.
 *
 * It is not possible as of Typescript 5.2 to declare a type for an enum key or value in-line.
 * Therefore, we must test the enum via a helper function that abstracts the enum to an object.
 *
 * If the test fails, you should review your `enum` to verify that both the
 * keys and values match the format specified by the `FlagKey` template literal type.
 * ref: https://stackoverflow.com/a/58181315
 *
 * @param testEnum - the Enum to type check
 */
export function testFlagEnumValidity(_) { }
export var FeatureFlagsKeysEnum;
(function (FeatureFlagsKeysEnum) {
    // Boolean flags
    FeatureFlagsKeysEnum["IS_API_IDEMPOTENCY_ENABLED"] = "IS_API_IDEMPOTENCY_ENABLED";
    FeatureFlagsKeysEnum["IS_API_RATE_LIMITING_DRY_RUN_ENABLED"] = "IS_API_RATE_LIMITING_DRY_RUN_ENABLED";
    FeatureFlagsKeysEnum["IS_API_RATE_LIMITING_KEYLESS_DRY_RUN_ENABLED"] = "IS_API_RATE_LIMITING_KEYLESS_DRY_RUN_ENABLED";
    FeatureFlagsKeysEnum["IS_API_RATE_LIMITING_ENABLED"] = "IS_API_RATE_LIMITING_ENABLED";
    FeatureFlagsKeysEnum["IS_CLOUDFLARE_SOCKETS_ENABLED"] = "IS_CLOUDFLARE_SOCKETS_ENABLED";
    FeatureFlagsKeysEnum["IS_LEGACY_WS_SERVICE_DISABLED"] = "IS_LEGACY_WS_SERVICE_DISABLED";
    FeatureFlagsKeysEnum["IS_EMAIL_INLINE_CSS_DISABLED"] = "IS_EMAIL_INLINE_CSS_DISABLED";
    FeatureFlagsKeysEnum["IS_EVENT_QUOTA_THROTTLER_ENABLED"] = "IS_EVENT_QUOTA_THROTTLER_ENABLED";
    FeatureFlagsKeysEnum["IS_NEW_MESSAGES_API_RESPONSE_ENABLED"] = "IS_NEW_MESSAGES_API_RESPONSE_ENABLED";
    FeatureFlagsKeysEnum["IS_USAGE_ALERTS_ENABLED"] = "IS_USAGE_ALERTS_ENABLED";
    /** Stops the usage-alerts cron before it reads usage. Default off, so the cron runs. */
    FeatureFlagsKeysEnum["IS_USAGE_ALERTS_CRON_DISABLED"] = "IS_USAGE_ALERTS_CRON_DISABLED";
    /** Global: the usage-alerts cron evaluates and logs every alert it would send, but sends none and writes no alert claims. */
    FeatureFlagsKeysEnum["IS_USAGE_ALERTS_DRY_RUN_ENABLED"] = "IS_USAGE_ALERTS_DRY_RUN_ENABLED";
    /**
     * Organization-scoped: workflow-run usage limits (on-demand limit, pause at limit, alert settings) in the
     * dashboard and `/billing/usage-limits`, paid-tier enforcement at the limit, and the usage-alerts cron honoring
     * those settings.
     */
    FeatureFlagsKeysEnum["IS_WORKFLOW_RUN_USAGE_LIMITS_ENABLED"] = "IS_WORKFLOW_RUN_USAGE_LIMITS_ENABLED";
    /**
     * Organization-scoped, read only when subscriber-process jobs are enqueued (`TriggerBase`): moves the usage
     * counter increment from enqueue to workflow-run creation (`CreateNotificationJobs`), matching what ClickHouse
     * counts. Transitional (NV-8853): remove with the enqueue-time increment and the job stamp once on for all
     * organizations and the subscriber-process queue has drained.
     */
    FeatureFlagsKeysEnum["IS_USAGE_COUNTER_WORKER_INCREMENT_ENABLED"] = "IS_USAGE_COUNTER_WORKER_INCREMENT_ENABLED";
    FeatureFlagsKeysEnum["IS_USE_MERGED_DIGEST_ID_ENABLED"] = "IS_USE_MERGED_DIGEST_ID_ENABLED";
    FeatureFlagsKeysEnum["IS_V2_ENABLED"] = "IS_V2_ENABLED";
    FeatureFlagsKeysEnum["IS_WORKFLOW_NODE_PREVIEW_ENABLED"] = "IS_WORKFLOW_NODE_PREVIEW_ENABLED";
    FeatureFlagsKeysEnum["IS_WEBHOOKS_MANAGEMENT_ENABLED"] = "IS_WEBHOOKS_MANAGEMENT_ENABLED";
    FeatureFlagsKeysEnum["IS_KEYLESS_ENVIRONMENT_CREATION_ENABLED"] = "IS_KEYLESS_ENVIRONMENT_CREATION_ENABLED";
    /** Dashboard "Local" pseudo-environment for previewing workflows from a local bridge (replaces the legacy local studio). */
    FeatureFlagsKeysEnum["IS_LOCAL_ENVIRONMENT_ENABLED"] = "IS_LOCAL_ENVIRONMENT_ENABLED";
    /**
     * Rich Chat: gates the block editor in the dashboard, Maily→card compilation in the API renderer,
     * and native card delivery in the worker.
     */
    FeatureFlagsKeysEnum["IS_CHAT_BLOCK_EDITOR_ENABLED"] = "IS_CHAT_BLOCK_EDITOR_ENABLED";
    FeatureFlagsKeysEnum["IS_KEYLESS_AGENT_AI_ENABLED"] = "IS_KEYLESS_AGENT_AI_ENABLED";
    /** When enabled, API-key auth on GET /v1/environments returns decrypted apiKeys for every environment in the org (pre-NV-7641 opt-in behavior). */
    FeatureFlagsKeysEnum["IS_LIST_ENVIRONMENTS_API_KEYS_ENABLED"] = "IS_LIST_ENVIRONMENTS_API_KEYS_ENABLED";
    FeatureFlagsKeysEnum["IS_TEST_PROVIDER_LIMITS_ENABLED"] = "IS_TEST_PROVIDER_LIMITS_ENABLED";
    FeatureFlagsKeysEnum["IS_2025_Q1_LEGACY_TIERING_MIGRATION"] = "IS_2025_Q1_LEGACY_TIERING_MIGRATION";
    FeatureFlagsKeysEnum["IS_SUBSCRIBER_ID_VALIDATION_DRY_RUN_ENABLED"] = "IS_SUBSCRIBER_ID_VALIDATION_DRY_RUN_ENABLED";
    FeatureFlagsKeysEnum["IS_TOPIC_KEYS_VALIDATION_DRY_RUN_ENABLED"] = "IS_TOPIC_KEYS_VALIDATION_DRY_RUN_ENABLED";
    FeatureFlagsKeysEnum["IS_RBAC_ENABLED"] = "IS_RBAC_ENABLED";
    FeatureFlagsKeysEnum["IS_HTTP_LOGS_PAGE_ENABLED"] = "IS_HTTP_LOGS_PAGE_ENABLED";
    FeatureFlagsKeysEnum["IS_INBOUND_LOGS_ENABLED"] = "IS_INBOUND_LOGS_ENABLED";
    FeatureFlagsKeysEnum["IS_TRACE_LOGS_ENABLED"] = "IS_TRACE_LOGS_ENABLED";
    FeatureFlagsKeysEnum["IS_TRACE_LOGS_READ_ENABLED"] = "IS_TRACE_LOGS_READ_ENABLED";
    FeatureFlagsKeysEnum["IS_INBOUND_WEBHOOKS_ENABLED"] = "IS_INBOUND_WEBHOOKS_ENABLED";
    FeatureFlagsKeysEnum["IS_INBOUND_WEBHOOK_ATTACHMENT_URLS_ENABLED"] = "IS_INBOUND_WEBHOOK_ATTACHMENT_URLS_ENABLED";
    FeatureFlagsKeysEnum["IS_INBOUND_WEBHOOKS_CONFIGURATION_ENABLED"] = "IS_INBOUND_WEBHOOKS_CONFIGURATION_ENABLED";
    FeatureFlagsKeysEnum["IS_STEP_RUN_LOGS_READ_ENABLED"] = "IS_STEP_RUN_LOGS_READ_ENABLED";
    FeatureFlagsKeysEnum["IS_STEP_RUN_LOGS_WRITE_ENABLED"] = "IS_STEP_RUN_LOGS_WRITE_ENABLED";
    FeatureFlagsKeysEnum["IS_WORKFLOW_RUN_LOGS_WRITE_ENABLED"] = "IS_WORKFLOW_RUN_LOGS_WRITE_ENABLED";
    FeatureFlagsKeysEnum["IS_WORKFLOW_RUN_LOGS_READ_ENABLED"] = "IS_WORKFLOW_RUN_LOGS_READ_ENABLED";
    FeatureFlagsKeysEnum["IS_WORKFLOW_RUN_TRACES_WRITE_ENABLED"] = "IS_WORKFLOW_RUN_TRACES_WRITE_ENABLED";
    FeatureFlagsKeysEnum["IS_WORKFLOW_RUN_PAGE_MIGRATION_ENABLED"] = "IS_WORKFLOW_RUN_PAGE_MIGRATION_ENABLED";
    FeatureFlagsKeysEnum["IS_WORKFLOW_RUN_COUNT_ENABLED"] = "IS_WORKFLOW_RUN_COUNT_ENABLED";
    FeatureFlagsKeysEnum["IS_DELIVERY_LIFECYCLE_TRANSITION_ENABLED"] = "IS_DELIVERY_LIFECYCLE_TRANSITION_ENABLED";
    FeatureFlagsKeysEnum["IS_EXECUTION_DETAILS_CLICKHOUSE_ONLY_ENABLED"] = "IS_EXECUTION_DETAILS_CLICKHOUSE_ONLY_ENABLED";
    FeatureFlagsKeysEnum["IS_GET_PREFERENCES_DISABLED"] = "IS_GET_PREFERENCES_DISABLED";
    FeatureFlagsKeysEnum["IS_REGION_SELECTOR_ENABLED"] = "IS_REGION_SELECTOR_ENABLED";
    FeatureFlagsKeysEnum["IS_PUSH_UNREAD_COUNT_ENABLED"] = "IS_PUSH_UNREAD_COUNT_ENABLED";
    FeatureFlagsKeysEnum["IS_EXPIRED_TOKENS_REMOVAL_ENABLED"] = "IS_EXPIRED_TOKENS_REMOVAL_ENABLED";
    FeatureFlagsKeysEnum["IS_ANALYTICS_WORKFLOW_FILTER_ENABLED"] = "IS_ANALYTICS_WORKFLOW_FILTER_ENABLED";
    FeatureFlagsKeysEnum["IS_CONTEXTUAL_HELP_DRAWER_ENABLED"] = "IS_CONTEXTUAL_HELP_DRAWER_ENABLED";
    FeatureFlagsKeysEnum["IS_SUBSCRIPTION_PREFERENCES_ENABLED"] = "IS_SUBSCRIPTION_PREFERENCES_ENABLED";
    FeatureFlagsKeysEnum["IS_LRU_CACHE_ENABLED"] = "IS_LRU_CACHE_ENABLED";
    FeatureFlagsKeysEnum["IS_CONTEXT_PREFERENCES_ENABLED"] = "IS_CONTEXT_PREFERENCES_ENABLED";
    /** When true, integration lookup may match across environments in the same organization (opt-in for regressed customers). Default is false (environment-scoped). */
    FeatureFlagsKeysEnum["IS_CROSS_ENVIRONMENT_INTEGRATION_ENABLED"] = "IS_CROSS_ENVIRONMENT_INTEGRATION_ENABLED";
    FeatureFlagsKeysEnum["IS_PREFERENCE_FETCH_OPTIMIZATION_ENABLED"] = "IS_PREFERENCE_FETCH_OPTIMIZATION_ENABLED";
    /** Platform-wide billing usage (Stripe usage records) source. Keep off until shadow mode proves parity. */
    FeatureFlagsKeysEnum["IS_BILLING_USAGE_CLICKHOUSE_ENABLED"] = "IS_BILLING_USAGE_CLICKHOUSE_ENABLED";
    /** Organization-scoped usage source for the event quota check and dashboard subscription usage. */
    FeatureFlagsKeysEnum["IS_BILLING_QUOTA_CLICKHOUSE_ENABLED"] = "IS_BILLING_QUOTA_CLICKHOUSE_ENABLED";
    FeatureFlagsKeysEnum["IS_BILLING_USAGE_CLICKHOUSE_SHADOW_ENABLED"] = "IS_BILLING_USAGE_CLICKHOUSE_SHADOW_ENABLED";
    FeatureFlagsKeysEnum["IS_BILLING_USAGE_DETAILED_DIAGNOSTICS_ENABLED"] = "IS_BILLING_USAGE_DETAILED_DIAGNOSTICS_ENABLED";
    FeatureFlagsKeysEnum["IS_AI_WORKFLOW_GENERATION_ENABLED"] = "IS_AI_WORKFLOW_GENERATION_ENABLED";
    /** Enable the Novu Wizard LLM Gateway (`POST /v2/llm/messages`) for an organization. Enterprise-only. */
    FeatureFlagsKeysEnum["IS_LLM_GATEWAY_ENABLED"] = "IS_LLM_GATEWAY_ENABLED";
    FeatureFlagsKeysEnum["IS_CLICKHOUSE_BATCHING_ENABLED"] = "IS_CLICKHOUSE_BATCHING_ENABLED";
    FeatureFlagsKeysEnum["IS_ORG_KILLSWITCH_FLAG_ENABLED"] = "IS_ORG_KILLSWITCH_FLAG_ENABLED";
    FeatureFlagsKeysEnum["IS_USAGE_REPORT_ENABLED"] = "IS_USAGE_REPORT_ENABLED";
    FeatureFlagsKeysEnum["IS_USAGE_REPORT_DELAY_ENABLED"] = "IS_USAGE_REPORT_DELAY_ENABLED";
    FeatureFlagsKeysEnum["IS_STEP_RESOLVER_ENABLED"] = "IS_STEP_RESOLVER_ENABLED";
    FeatureFlagsKeysEnum["IS_ACTION_STEP_RESOLVER_ENABLED"] = "IS_ACTION_STEP_RESOLVER_ENABLED";
    /** Enable conversational Agents UI in the dashboard; create the boolean in LaunchDarkly for cloud, or set `VITE_IS_CONVERSATIONAL_AGENTS_ENABLED` when self-hosted. */
    FeatureFlagsKeysEnum["IS_CONVERSATIONAL_AGENTS_ENABLED"] = "IS_CONVERSATIONAL_AGENTS_ENABLED";
    /** Enable WhatsApp Embedded Signup (Meta Tech Provider) in the agent onboarding guide; create the boolean in LaunchDarkly for cloud, or set `VITE_IS_WHATSAPP_EMBEDDED_SIGNUP_ENABLED` when self-hosted. */
    FeatureFlagsKeysEnum["IS_WHATSAPP_EMBEDDED_SIGNUP_ENABLED"] = "IS_WHATSAPP_EMBEDDED_SIGNUP_ENABLED";
    /** Enable managed-runtime mode for Agents (e.g. Claude Platform). Create the boolean in LaunchDarkly for cloud, or set `VITE_IS_MANAGED_AGENT_RUNTIME_ENABLED` when self-hosted. */
    FeatureFlagsKeysEnum["IS_MANAGED_AGENT_RUNTIME_ENABLED"] = "IS_MANAGED_AGENT_RUNTIME_ENABLED";
    /** Enable Novu-managed demo Claude provider auto-provisioned on dev environments. Create the boolean in LaunchDarkly for cloud, or set `VITE_IS_DEMO_MANAGED_CLAUDE_ENABLED` when self-hosted. */
    FeatureFlagsKeysEnum["IS_DEMO_MANAGED_CLAUDE_ENABLED"] = "IS_DEMO_MANAGED_CLAUDE_ENABLED";
    /**
     * Enable framework `ctx.ask` / `ctx.approve` / `ctx.choose` / `ctx.tell` human
     * interactions delivered into the agent conversation. Create the boolean in
     * LaunchDarkly for cloud, or set `IS_AGENT_HUMAN_HITL_ENABLED` when self-hosted.
     */
    FeatureFlagsKeysEnum["IS_AGENT_HUMAN_HITL_ENABLED"] = "IS_AGENT_HUMAN_HITL_ENABLED";
    /**
     * Enable the Web Chat channel (subscriber `/v1/web-chat/*`, useWebChat wayfinder).
     * Requires conversational agents. Create the boolean in LaunchDarkly for cloud, or set
     * `IS_AGENT_WEB_CHAT_ENABLED` when self-hosted (`VITE_IS_AGENT_WEB_CHAT_ENABLED` for dashboard).
     * Flag key kept as IS_AGENT_WEB_CHAT_ENABLED (LaunchDarkly / env already deployed).
     */
    FeatureFlagsKeysEnum["IS_AGENT_WEB_CHAT_ENABLED"] = "IS_AGENT_WEB_CHAT_ENABLED";
    /** Enable the "What's next" section on the agent overview. Create the boolean in LaunchDarkly for cloud, or set `VITE_IS_AGENT_WHATS_NEXT_ENABLED` when self-hosted. */
    FeatureFlagsKeysEnum["IS_AGENT_WHATS_NEXT_ENABLED"] = "IS_AGENT_WHATS_NEXT_ENABLED";
    /** Enable the MS Teams subscriber-rollout "What's next" guide (distribute the bot + connect end users) and its post-connect "Continue" CTA. When off, MS Teams shows the generic continue note and hides the rollout guide. Create the boolean in LaunchDarkly for cloud, or set `VITE_IS_AGENT_MSTEAMS_WHATS_NEXT_ENABLED` when self-hosted. */
    FeatureFlagsKeysEnum["IS_AGENT_MSTEAMS_WHATS_NEXT_ENABLED"] = "IS_AGENT_MSTEAMS_WHATS_NEXT_ENABLED";
    /** Enable the production-readiness "What's next" guide for the Novu email agent (own provider, verified domain/deliverability, branded inbound address, custom From, user rollout) and simplify the out-of-box email setup to address + test on cloud. When off, the email integration keeps its current single-guide behavior. Create the boolean in LaunchDarkly for cloud, or set `VITE_IS_AGENT_EMAIL_WHATS_NEXT_ENABLED` when self-hosted. */
    FeatureFlagsKeysEnum["IS_AGENT_EMAIL_WHATS_NEXT_ENABLED"] = "IS_AGENT_EMAIL_WHATS_NEXT_ENABLED";
    /** Enable Microsoft Teams Quick Setup in the dashboard; create the boolean in LaunchDarkly for cloud, or set `VITE_IS_MSTEAMS_QUICK_SETUP_ENABLED` when self-hosted. */
    FeatureFlagsKeysEnum["IS_MSTEAMS_QUICK_SETUP_ENABLED"] = "IS_MSTEAMS_QUICK_SETUP_ENABLED";
    /** Enable Slack Quick Setup in the dashboard; create the boolean in LaunchDarkly for cloud, or set `VITE_IS_SLACK_QUICK_SETUP_ENABLED` when self-hosted. */
    FeatureFlagsKeysEnum["IS_SLACK_QUICK_SETUP_ENABLED"] = "IS_SLACK_QUICK_SETUP_ENABLED";
    /**
     * Enable NovuCopilot on Slack — the Novu-hosted Slack agent that lets a customer create Novu
     * workflows by chatting in Slack.
     */
    FeatureFlagsKeysEnum["IS_NOVU_COPILOT_SLACK_ENABLED"] = "IS_NOVU_COPILOT_SLACK_ENABLED";
    /**
     * Route the Copilot's workflow tool calls (Slack Copilot and dashboard sidekick) through the
     * in-process MCP workflow tools (`@novu/mcp-server/tools`) and the API over loopback, instead of
     * calling the workflow use cases directly. Per organization, default off. Needs
     * `NOVU_INTERNAL_AUTH_SECRET`; without it the Copilot keeps the direct path.
     */
    FeatureFlagsKeysEnum["IS_COPILOT_MCP_TOOLS_ENABLED"] = "IS_COPILOT_MCP_TOOLS_ENABLED";
    /** Enable the Domains management page in the dashboard. */
    FeatureFlagsKeysEnum["IS_DOMAINS_PAGE_ENABLED"] = "IS_DOMAINS_PAGE_ENABLED";
    /** Enable Domain Connect auto-configuration for inbound email domains. */
    FeatureFlagsKeysEnum["IS_DOMAIN_CONNECT_INBOUND_EMAIL_ENABLED"] = "IS_DOMAIN_CONNECT_INBOUND_EMAIL_ENABLED";
    /**
     * Enable MCP connections that use Novu's pre-registered OAuth app
     * (`auth mode = 'novu-app'`). Gates both `EnableAgentMcpServer` for any
     * catalog entry whose `oauth.mode === 'novu-app'` and `GenerateMcpOAuthUrl`
     * when it resolves to the novu-app branch. DCR connections are never gated
     * by this flag.
     */
    FeatureFlagsKeysEnum["IS_MCP_NOVU_APP_ENABLED"] = "IS_MCP_NOVU_APP_ENABLED";
    /**
     * Enable MCP connections where OAuth is fully delegated to the managed
     * agent runtime provider (`auth mode = 'provider-managed'`). Gates the
     * "Add from Claude" flow in the dashboard and the
     * `POST .../mcp-servers/:mcpId/provider-vault` endpoint. When off,
     * provider-managed catalog rows still appear in the picker but the Add
     * button is disabled.
     */
    FeatureFlagsKeysEnum["IS_MCP_PROVIDER_MANAGED_ENABLED"] = "IS_MCP_PROVIDER_MANAGED_ENABLED";
    /**
     * Temporary flag for rolling secret key rotation. When enabled, customers can
     * create a second API key (`POST /v1/environments/api-keys`) and delete a specific
     * key (`DELETE /v1/environments/api-keys/:hash`) so they can rotate keys without
     * downtime. Also gates the multi-key UI on the dashboard API Keys page
     * (`VITE_IS_MULTIPLE_SECRET_KEYS_ALLOWED` when self-hosted). Enable per
     * organization for the duration of the rotation, then disable.
     */
    FeatureFlagsKeysEnum["IS_MULTIPLE_SECRET_KEYS_ALLOWED"] = "IS_MULTIPLE_SECRET_KEYS_ALLOWED";
    /**
     * Stop duplicating the trigger payload onto every job and every non-in-app
     * message. When enabled, new jobs and email/SMS/push messages no longer
     * persist `payload`; readers resolve it from the parent notification via
     * `_notificationId`. In-app messages keep their payload for legacy feed
     * filtering. Read paths handle both shapes regardless of this flag, so it can
     * be toggled off at any time (forward-only, no data migration). Create the
     * boolean in LaunchDarkly for cloud, or set `IS_PAYLOAD_DEDUP_ENABLED` when
     * self-hosted.
     */
    FeatureFlagsKeysEnum["IS_PAYLOAD_DEDUP_ENABLED"] = "IS_PAYLOAD_DEDUP_ENABLED";
    /**
     * Trace step condition evaluation in the activity feed regardless of outcome:
     * a "step conditions matched" detail when conditions pass, and a "step was
     * skipped based on steps conditions" detail when they do not (v2 skip
     * conditions, HTTP Request steps, and legacy v1 filters including webhook
     * filters). Legacy v1 skipped steps still persist their skip detail when
     * this flag is off. Create the boolean in LaunchDarkly for cloud, or set
     * `IS_STEP_CONDITIONS_EVALUATION_TRACE_ENABLED` when self-hosted.
     */
    FeatureFlagsKeysEnum["IS_STEP_CONDITIONS_EVALUATION_TRACE_ENABLED"] = "IS_STEP_CONDITIONS_EVALUATION_TRACE_ENABLED";
    /**
     * Stop embedding the fully populated workflow step (message template
     * `content`, `controls`, `cta`, `variables`, variants' templates, `output`
     * schemas, etc.) onto every job's `step`. When enabled, new jobs persist a
     * lean step (ids, filters, metadata, a `{ _id, type }` template stub) and the
     * worker rehydrates the full template at execution time (see
     * StepTemplateHydrationService for the resolution/fallback order). Jobs
     * written while the flag is off (and all pre-existing jobs) carry the full
     * snapshot and skip hydration entirely, so the flag is forward-only and safe
     * to toggle off at any time (no data migration). Create the boolean in
     * LaunchDarkly for cloud, or set `IS_JOB_STEP_DEDUP_ENABLED` when self-hosted.
     */
    FeatureFlagsKeysEnum["IS_JOB_STEP_DEDUP_ENABLED"] = "IS_JOB_STEP_DEDUP_ENABLED";
    /** Enable the Tool channel (PagerDuty, Opsgenie, and custom webhooks). */
    FeatureFlagsKeysEnum["IS_TOOL_CHANNEL_ENABLED"] = "IS_TOOL_CHANNEL_ENABLED";
    /**
     * Enable assigning an agent to a workflow in the dashboard ("Send & reply via agent").
     * Create the boolean in LaunchDarkly for cloud, or set
     * `VITE_IS_WORKFLOW_AGENT_ASSIGNMENT_ENABLED` when self-hosted.
     */
    FeatureFlagsKeysEnum["IS_WORKFLOW_AGENT_ASSIGNMENT_ENABLED"] = "IS_WORKFLOW_AGENT_ASSIGNMENT_ENABLED";
    /** Enable per-provider content overrides on chat steps (Slack schema, free-form elsewhere). */
    FeatureFlagsKeysEnum["IS_CHAT_PROVIDER_OVERRIDES_ENABLED"] = "IS_CHAT_PROVIDER_OVERRIDES_ENABLED";
    /**
     * Show per-integration content overrides on chat and tool steps in the dashboard. Stored
     * integration overrides are always applied at send time. Create the boolean in LaunchDarkly
     * for cloud, or set `VITE_IS_INTEGRATION_CONTENT_OVERRIDES_ENABLED` when self-hosted.
     */
    FeatureFlagsKeysEnum["IS_INTEGRATION_CONTENT_OVERRIDES_ENABLED"] = "IS_INTEGRATION_CONTENT_OVERRIDES_ENABLED";
    /** Enable per-provider content overrides on push steps (FCM/Expo schemas, free-form elsewhere). */
    FeatureFlagsKeysEnum["IS_PUSH_PROVIDER_OVERRIDES_ENABLED"] = "IS_PUSH_PROVIDER_OVERRIDES_ENABLED";
    /**
     * When true (default), the deprecated per-subscriber chat OAuth routes require
     * HMAC to be enabled on the Slack integration and a valid subscriber HMAC hash.
     * Target legacy organizations to false in LaunchDarkly so they can keep the
     * historical behavior during migration. Self-hosted: set
     * `IS_SUBSCRIBER_CHAT_OAUTH_HMAC_REQUIRED_ENABLED=false` to disable enforcement.
     */
    FeatureFlagsKeysEnum["IS_SUBSCRIBER_CHAT_OAUTH_HMAC_REQUIRED_ENABLED"] = "IS_SUBSCRIBER_CHAT_OAUTH_HMAC_REQUIRED_ENABLED";
    // String flags
    FeatureFlagsKeysEnum["USAGE_REPORT_TRIGGER_SECRET"] = "USAGE_REPORT_TRIGGER_SECRET";
    FeatureFlagsKeysEnum["USAGE_REPORT_OVERRIDE_EMAIL"] = "USAGE_REPORT_OVERRIDE_EMAIL";
    // Numeric flags
    FeatureFlagsKeysEnum["MAX_WORKFLOW_LIMIT_NUMBER"] = "MAX_WORKFLOW_LIMIT_NUMBER";
    FeatureFlagsKeysEnum["MAX_LAYOUT_LIMIT_NUMBER"] = "MAX_LAYOUT_LIMIT_NUMBER";
    FeatureFlagsKeysEnum["MAX_STEPS_PER_WORKFLOW_LIMIT_NUMBER"] = "MAX_STEPS_PER_WORKFLOW_LIMIT_NUMBER";
    FeatureFlagsKeysEnum["MAX_DEFER_DURATION_IN_MS_NUMBER"] = "MAX_DEFER_DURATION_IN_MS_NUMBER";
    FeatureFlagsKeysEnum["MAX_THROTTLE_WINDOW_DURATION_IN_MS_NUMBER"] = "MAX_THROTTLE_WINDOW_DURATION_IN_MS_NUMBER";
    FeatureFlagsKeysEnum["LOG_EXPIRATION_DAYS_NUMBER"] = "LOG_EXPIRATION_DAYS_NUMBER";
    FeatureFlagsKeysEnum["MAX_DATE_ANALYTICS_ENABLED_NUMBER"] = "MAX_DATE_ANALYTICS_ENABLED_NUMBER";
    FeatureFlagsKeysEnum["MAX_ENVIRONMENT_COUNT"] = "MAX_ENVIRONMENT_COUNT";
    FeatureFlagsKeysEnum["MAX_SUBSCRIBER_DEVICE_TOKENS_NUMBER"] = "MAX_SUBSCRIBER_DEVICE_TOKENS_NUMBER";
    FeatureFlagsKeysEnum["MAX_ENVIRONMENT_VARIABLES_LIMIT_NUMBER"] = "MAX_ENVIRONMENT_VARIABLES_LIMIT_NUMBER";
    FeatureFlagsKeysEnum["MAX_STEP_RESOLVERS_NUMBER"] = "MAX_STEP_RESOLVERS_NUMBER";
    /**
     * Max conditions (or nested groups) allowed in a single step-conditions group.
     * Default is 10 when the flag is unset, invalid, or below 1. Create the number
     * in LaunchDarkly for cloud, or set `VITE_MAX_STEP_CONDITIONS_PER_GROUP_NUMBER`
     * when self-hosted.
     */
    FeatureFlagsKeysEnum["MAX_STEP_CONDITIONS_PER_GROUP_NUMBER"] = "MAX_STEP_CONDITIONS_PER_GROUP_NUMBER";
    FeatureFlagsKeysEnum["MAX_DOMAINS_LIMIT_NUMBER"] = "MAX_DOMAINS_LIMIT_NUMBER";
    FeatureFlagsKeysEnum["MAX_AGENTS_LIMIT_NUMBER"] = "MAX_AGENTS_LIMIT_NUMBER";
    FeatureFlagsKeysEnum["MAX_CUSTOM_EMAIL_DOMAINS_NUMBER"] = "MAX_CUSTOM_EMAIL_DOMAINS_NUMBER";
    /**
     * When greater than 0, stands in for the included events under an on-demand limit above them,
     * replaces the tier-derived usage-alert cap otherwise, and lowers the candidate query to 75% of
     * this value. 0 keeps the real cap. Set it in the staging environment only.
     */
    FeatureFlagsKeysEnum["USAGE_ALERTS_ALLOWANCE_OVERRIDE_NUMBER"] = "USAGE_ALERTS_ALLOWANCE_OVERRIDE_NUMBER";
    FeatureFlagsKeysEnum["IS_ANALYTICS_PAGE_ENABLED"] = "IS_ANALYTICS_PAGE_ENABLED";
    FeatureFlagsKeysEnum["IS_LEGACY_SELECTOR_BUTTON_VISIBLE"] = "IS_LEGACY_SELECTOR_BUTTON_VISIBLE";
})(FeatureFlagsKeysEnum || (FeatureFlagsKeysEnum = {}));
//# sourceMappingURL=feature-flags.js.map